import { useEffect, useState } from "react";
import type { CctvCamera, CvCenter } from "@sikhio/shared";
import { parsePlayerUrl } from "../lib/cctvPlayer";
import { CctvMsePlayer } from "./CctvMsePlayer";

interface Props {
  apiBase: string;
  cameras: CctvCamera[];
  /** Per-camera CV state, lifted so the wall and the centre read one feed. */
  centres: CvCenter[];
  /** The wall's own open/closed state, lifted so App can decide what to poll. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOpenCentre: () => void;
  selectedCameraId?: string;
  onSelect: (cam: CctvCamera) => void;
}

/**
 * Camera wall — a horizontal strip docked at the top of the map with one
 * tile per camera.
 *
 * A tile shows the best evidence available for that camera, in order:
 *
 *   1. the CV probe's snapshot — a real frame pulled from the municipal
 *      NVR, with the detector's box drawn on it. This is the only tile
 *      that says something about what the camera is SEEING.
 *   2. the Longdo snapshot / NT live player, as before.
 *   3. a labelled launcher, when the camera exposes no image at all.
 *
 * Imagery first, launchers second, offline last; stable by id within each
 * group so tiles don't jump around on refetch. Exported for unit testing.
 */
export function sortCctvWall(a: CctvCamera, b: CctvCamera): number {
  const rank = (c: CctvCamera) =>
    c.online === false ? 2 : c.imageUrl ? 0 : 1;
  return rank(a) - rank(b) || (a.id < b.id ? -1 : 1);
}

function statusColor(c: CctvCamera): string {
  if (c.online === true) return "var(--good)";
  if (c.online === false) return "var(--bad)";
  return "var(--ink-3)";
}

export function CctvWall({
  apiBase,
  cameras,
  centres,
  open,
  onOpenChange,
  onOpenCentre,
  selectedCameraId,
  onSelect,
}: Props) {
  /* MSH: this opened expanded on every load, docking ~136px off the top of a
     764px map (17.8% of it) for a feature the operator had not asked for yet,
     before a single pixel of town was visible. Measured at 1440x900 with all
     27 cameras present. The strip's real job — "are the cameras up, and can I
     open one" — is answered by the header alone, so the header is the default
     and the tiles are one click away. */
  const [previewId, setPreviewId] = useState<string | null>(null);
  // Wall-wide snapshot tick — re-fetches Longdo frames without per-tile timers.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const current = cameras.find((cam) => cam.id === previewId && cam.online !== false && parsePlayerUrl(cam.playerUrl));
    if (current) return;
    const first = cameras.find((cam) => cam.vendor === "sikhio-nt" && cam.online !== false && parsePlayerUrl(cam.playerUrl));
    setPreviewId(first?.id ?? null);
  }, [cameras, previewId]);

  if (cameras.length === 0) return null;
  const ordered = [...cameras].sort(sortCctvWall);
  const previewIndex = previewId ? ordered.findIndex((cam) => cam.id === previewId) : -1;
  if (previewIndex > 0) ordered.unshift(ordered.splice(previewIndex, 1)[0]);
  const previewCamera = selectedCameraId
    ? null
    : cameras.find((cam) => cam.id === previewId && cam.vendor === "sikhio-nt" && cam.online !== false) ?? null;
  const previewStream = previewCamera ? parsePlayerUrl(previewCamera.playerUrl) : null;

  // The cameras the CV probe is watching, and what it last found in them.
  const centreByCamera = new Map(centres.map((c) => [c.cameraId, c]));
  const watched = centres.filter((c) => c.detections.length > 0).length;

  return (
    <section className={`cctv-wall${open ? "" : " cctv-wall--closed"}`} aria-label="Camera wall">
      <header className="cctv-wall-head">
        <button
          type="button"
          className="cctv-wall-toggle mono"
          onClick={() => onOpenChange(!open)}
          aria-expanded={open}
          aria-label={open ? "Collapse camera wall" : "Expand camera wall"}
        >
          {open ? "▾" : "▸"}
        </button>
        <span className="eyebrow mono">กล้อง · CAMERA WALL</span>
        <span className="mono cctv-wall-count">
          {/* Collapsed is now the default, so the header has to carry the
              answer on its own: how many cameras, and how many are up. */}
          {cameras.filter((c) => c.online !== false).length}/{cameras.length} ออนไลน์
        </span>
        {/* The computer-vision centre is one click from the only surface
            that already talks about cameras. It costs no pixels until it
            is opened. */}
        <button
          type="button"
          className="mono cctv-wall-centre"
          onClick={onOpenCentre}
          aria-haspopup="dialog"
          aria-label="เปิดศูนย์กล้องวิเคราะห์ภาพ"
        >
          ศูนย์กล้อง{watched > 0 ? ` · ${watched}` : ""}
        </button>
        {open && (
          <button
            type="button"
            className="mono cctv-wall-refresh"
            onClick={() => setTick((t) => t + 1)}
            aria-label="Refresh camera wall snapshots"
          >
            ↻
          </button>
        )}
      </header>
      {open && (
        <div className="cctv-wall-row" role="list">
          {ordered.map((cam) => {
            const preview = cam.id === previewCamera?.id && previewStream ? previewStream : null;
            const centre = centreByCamera.get(cam.id);
            const snapshot = centre?.snapshotUrl ?? null;
            return (
              <button
                key={cam.id}
                type="button"
                role="listitem"
                className="cctv-tile"
                onClick={() => {
                  if (cam.vendor === "sikhio-nt" && cam.online !== false) setPreviewId(cam.id);
                  onSelect(cam);
                }}
                aria-label={`Open live view: ${cam.name}`}
                title={cam.name}
              >
                {snapshot ? (
                  /* The probe's frame — the camera as it actually is. */
                  <span className="cctv-tile-preview">
                    <img
                      className="cctv-tile-img"
                      src={`${apiBase}${snapshot}`}
                      alt=""
                      loading="lazy"
                    />
                    <svg className="cctv-tile-boxes" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
                      {(centre?.detections ?? []).map((d, i) =>
                        d.bbox ? (
                          <rect
                            key={i}
                            x={d.bbox.x * 100}
                            y={d.bbox.y * 100}
                            width={d.bbox.w * 100}
                            height={d.bbox.h * 100}
                            className={d.class === "motion" ? "cv-box cv-box--motion" : "cv-box cv-box--presence"}
                          />
                        ) : null,
                      )}
                    </svg>
                  </span>
                ) : preview ? (
                  <span className="cctv-tile-preview">
                    <CctvMsePlayer stream={preview} name={cam.name} controls={false} />
                  </span>
                ) : (
                  <span className="cctv-tile-fallback" aria-hidden="true">
                    <span className="cctv-tile-glyph">◉</span>
                    {!cam.imageUrl && <span className="eyebrow mono">คลิกเพื่อดูภาพสด</span>}
                  </span>
                )}
                {cam.imageUrl && !snapshot && (
                  <img
                    key={`${cam.id}-${tick}`}
                    className="cctv-tile-img"
                    src={`${cam.imageUrl}${cam.imageUrl.includes("?") ? "&" : "?"}_=${tick}`}
                    alt=""
                    loading="lazy"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                )}
                <span className="cctv-tile-bar">
                  <span
                    className="cctv-state-dot"
                    style={{ background: statusColor(cam) }}
                    aria-hidden="true"
                  />
                  <span className="cctv-tile-name">{cam.name}</span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
